import React, { useEffect, useMemo, useState } from "react";
import { Download, RotateCcw, Search } from "lucide-react";
import { motion } from "framer-motion";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { GlassInput } from "@/components/ui/GlassInput";
import { NxButton } from "@/components/ui/NxButton";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { apiGet, apiGetWithMeta, API_BASE, getAccessToken } from "@/lib/api";
import pt from "@/lib/i18n";
import { formatRelative } from "@/lib/utils";

const PAGE_SIZE = 50;

const AuditLogsPage = () => {
    const [rows, setRows] = useState(null);
    const [meta, setMeta] = useState({ page: 1, total: 0 });
    const [actions, setActions] = useState([]);
    const [filters, setFilters] = useState({
        action: "",
        resource_type: "",
        date_from: "",
        date_to: "",
        search: "",
    });
    const [page, setPage] = useState(1);

    useEffect(() => {
        apiGet("/admin/audit-logs/actions").then(setActions).catch(() => setActions([]));
    }, []);

    const load = async () => {
        setRows(null);
        try {
            const params = new URLSearchParams();
            const a = filters.search || filters.action;
            if (a) params.set("action", a);
            if (filters.resource_type) params.set("resource_type", filters.resource_type);
            if (filters.date_from) params.set("date_from", filters.date_from);
            if (filters.date_to) params.set("date_to", filters.date_to + "T23:59:59Z");
            params.set("page", String(page));
            params.set("page_size", String(PAGE_SIZE));
            const { data, meta } = await apiGetWithMeta(`/admin/audit-logs?${params}`);
            setRows(data || []);
            setMeta(meta || { page, total: 0 });
        } catch (e) {
            setRows([]);
            setMeta({ page, total: 0 });
        }
    };

    useEffect(() => {
        const id = setTimeout(load, 300);
        return () => clearTimeout(id);
    }, [filters, page]);

    const resources = useMemo(
        () => ["user", "service", "ticket", "document", "invoice", "integration", "proposal", "deal"],
        [],
    );

    const totalPages = Math.max(1, Math.ceil((meta.total || 0) / PAGE_SIZE));

    const exportCsv = async () => {
        const params = new URLSearchParams();
        const a = filters.search || filters.action;
        if (a) params.set("action", a);
        if (filters.resource_type) params.set("resource_type", filters.resource_type);
        if (filters.date_from) params.set("date_from", filters.date_from);
        if (filters.date_to) params.set("date_to", filters.date_to + "T23:59:59Z");
        const url = `${API_BASE}/admin/audit-logs/export.csv?${params}`;
        const token = getAccessToken();
        try {
            const resp = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
                credentials: "include",
            });
            if (!resp.ok) throw new Error("export failed");
            const blob = await resp.blob();
            const dl = document.createElement("a");
            dl.href = URL.createObjectURL(blob);
            dl.download = `nexustel-audit-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(dl);
            dl.click();
            dl.remove();
        } catch (e) {
            /* swallow */
        }
    };

    const resetFilters = () => {
        setFilters({ action: "", resource_type: "", date_from: "", date_to: "", search: "" });
        setPage(1);
    };

    return (
        <div data-testid="audit-logs-page">
            <PageHeader title={pt.admin.audit.title} subtitle={pt.admin.audit.subtitle} testId="audit-header" />
            <GlassCard padding="p-4" className="mb-5" testId="audit-filters">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-4">
                        <GlassInput
                            prefix={<Search size={16} />}
                            placeholder={pt.admin.audit.search}
                            value={filters.search}
                            onChange={(e) => {
                                setPage(1);
                                setFilters({ ...filters, search: e.target.value });
                            }}
                            testId="audit-search"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <select
                            value={filters.action}
                            onChange={(e) => {
                                setPage(1);
                                setFilters({ ...filters, action: e.target.value, search: "" });
                            }}
                            className="glass-input"
                            data-testid="audit-action-filter"
                        >
                            <option value="">{pt.admin.audit.allActions}</option>
                            {actions.map((a) => (
                                <option key={a} value={a}>
                                    {a}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <select
                            value={filters.resource_type}
                            onChange={(e) => {
                                setPage(1);
                                setFilters({ ...filters, resource_type: e.target.value });
                            }}
                            className="glass-input"
                            data-testid="audit-resource-filter"
                        >
                            <option value="">{pt.admin.audit.allResources}</option>
                            {resources.map((r) => (
                                <option key={r} value={r}>
                                    {r}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-[color:var(--slate-light)] mb-1.5 font-medium">
                            {pt.admin.audit.from}
                        </label>
                        <input
                            type="date"
                            value={filters.date_from}
                            onChange={(e) => {
                                setPage(1);
                                setFilters({ ...filters, date_from: e.target.value });
                            }}
                            className="glass-input"
                            data-testid="audit-date-from"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-[color:var(--slate-light)] mb-1.5 font-medium">
                            {pt.admin.audit.to}
                        </label>
                        <input
                            type="date"
                            value={filters.date_to}
                            onChange={(e) => {
                                setPage(1);
                                setFilters({ ...filters, date_to: e.target.value });
                            }}
                            className="glass-input"
                            data-testid="audit-date-to"
                        />
                    </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-[color:var(--glass-border-subtle)]">
                    <button
                        type="button"
                        onClick={resetFilters}
                        className="inline-flex items-center gap-1.5 text-xs text-[color:var(--slate-mid)] hover:text-[color:var(--copper-dark)] font-body"
                        data-testid="audit-reset"
                    >
                        <RotateCcw size={13} /> {pt.admin.audit.reset}
                    </button>
                    <NxButton variant="copper" size="sm" onClick={exportCsv} icon={<Download size={14} />} testId="audit-export-csv">
                        {pt.admin.audit.export}
                    </NxButton>
                </div>
            </GlassCard>

            {rows === null ? (
                <GlassCard padding="p-6">
                    <div className="space-y-3">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <Skeleton key={i} height="44px" />
                        ))}
                    </div>
                </GlassCard>
            ) : rows.length === 0 ? (
                <EmptyState testId="audit-empty" title={pt.admin.audit.empty} icon={<Search size={22} />} />
            ) : (
                <GlassCard padding="p-0" testId="audit-table">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-[color:var(--glass-border)]">
                                    {[pt.admin.audit.col_when, pt.admin.audit.col_user, pt.admin.audit.col_action, pt.admin.audit.col_resource, pt.admin.audit.col_ip, pt.admin.audit.col_details].map((h) => (
                                        <th
                                            key={h}
                                            className="text-left px-5 py-3 font-body text-[11px] uppercase tracking-[0.16em] text-[color:var(--slate-light)] font-medium"
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, idx) => (
                                    <motion.tr
                                        key={r.id || idx}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: Math.min(idx * 0.01, 0.2) }}
                                        className="border-b border-[color:var(--glass-border-subtle)] hover:bg-[color:var(--copper-subtle)] transition-colors"
                                        data-testid={`audit-row-${r.id || idx}`}
                                    >
                                        <td className="px-5 py-3 font-mono text-xs text-[color:var(--slate-mid)] whitespace-nowrap">
                                            {formatRelative(r.created_at)}
                                        </td>
                                        <td className="px-5 py-3 text-sm">
                                            {r.user ? (
                                                <div className="flex flex-col">
                                                    <span className="text-[color:var(--slate-deep)] font-medium">
                                                        {r.user.full_name}
                                                    </span>
                                                    <span className="font-mono text-[10px] text-[color:var(--slate-light)]">
                                                        {r.user.email}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-[color:var(--slate-light)] italic">
                                                    {pt.admin.audit.system}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3">
                                            <span className="font-mono text-xs px-2 py-1 rounded bg-[color:var(--copper-subtle)] text-[color:var(--copper-dark)]">
                                                {r.action}
                                            </span>
                                        </td>
                                        <td className="px-5 py-3 font-mono text-xs text-[color:var(--slate-mid)]">
                                            {r.resource_type || "—"}
                                        </td>
                                        <td className="px-5 py-3 font-mono text-xs text-[color:var(--slate-light)]">
                                            {r.ip_address || "—"}
                                        </td>
                                        <td className="px-5 py-3 font-mono text-[11px] text-[color:var(--slate-mid)] max-w-[280px] truncate">
                                            {r.details ? JSON.stringify(r.details) : "—"}
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-5 py-3 border-t border-[color:var(--glass-border-subtle)]">
                            <span className="font-body text-xs text-[color:var(--slate-mid)]">
                                {pt.common.page} {meta.page} {pt.common.of} {totalPages} · {meta.total} registos
                            </span>
                            <div className="flex gap-2">
                                <NxButton variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} testId="audit-prev">
                                    ← Anterior
                                </NxButton>
                                <NxButton variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} testId="audit-next">
                                    Seguinte →
                                </NxButton>
                            </div>
                        </div>
                    )}
                </GlassCard>
            )}
        </div>
    );
};

export default AuditLogsPage;
